/**
 * Who the signed-in web-app user is acting as: themselves or one of their approved organizations
 * (the mobile drawer's "switch to organization"). Separate from `organizations/selectedOrganization`,
 * which belongs to the CMS.
 *
 * Kept per tab in sessionStorage: it survives reloads and payment redirects, a new tab starts as
 * the user, and other tabs (or the CMS) are never switched behind the user's back.
 */
export interface ActingOrganization {
  id: string;
  name: string;
  avatar?: string | null;
  organization_type?: string | null;
}

export type ActingEntityType = 'User' | 'Organization';

interface ActingEntityState {
  organization: ActingOrganization | null;
  organizations: ActingOrganization[];
  organizationsLoaded: boolean;
}

export const ACTING_ORGANIZATION_STORAGE_KEY = 'webActingOrganization';

function readStoredOrganization(): ActingOrganization | null {
  try {
    const raw = sessionStorage.getItem(ACTING_ORGANIZATION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.id ? parsed : null;
  } catch {
    return null;
  }
}

function writeStoredOrganization(organization: ActingOrganization | null) {
  try {
    if (organization) {
      sessionStorage.setItem(ACTING_ORGANIZATION_STORAGE_KEY, JSON.stringify(organization));
    } else {
      sessionStorage.removeItem(ACTING_ORGANIZATION_STORAGE_KEY);
    }
  } catch {
    // storage unavailable: the choice lasts until the page reloads
  }
}

/** Keeps only what the switcher shows, from `profile.user.organizations`. */
export function toActingOrganization(org: any): ActingOrganization | null {
  if (!org?.id) return null;
  return {
    id: String(org.id),
    name: org.name ?? '',
    avatar: org.avatar ?? null,
    organization_type: org.organization_type ?? null,
  };
}

/** Same rule as mobile (`UserOrgCubit`): only approved organizations can be switched to. */
export function switchableOrganizations(organizations: any[] | null | undefined) {
  return (organizations ?? [])
    .filter((org) => org?.approved === true)
    .map(toActingOrganization)
    .filter((org): org is ActingOrganization => !!org);
}

const state = (): ActingEntityState => ({
  organization: readStoredOrganization(),
  organizations: [],
  organizationsLoaded: false,
});

const mutations = {
  SET_ORGANIZATION(s: ActingEntityState, organization: ActingOrganization | null) {
    s.organization = organization;
    writeStoredOrganization(organization);
  },
  SET_ORGANIZATIONS(s: ActingEntityState, organizations: ActingOrganization[]) {
    s.organizations = organizations;
    s.organizationsLoaded = true;
  },
  RESET(s: ActingEntityState) {
    s.organization = null;
    s.organizations = [];
    s.organizationsLoaded = false;
    writeStoredOrganization(null);
  },
};

const actions = {
  selectOrganization({ commit }: any, organization: ActingOrganization | null) {
    commit('SET_ORGANIZATION', organization ? toActingOrganization(organization) : null);
  },

  /**
   * Stores the user's switchable organizations. Drops the acting organization when the user is
   * no longer one of its approved admins, and refreshes its name/avatar otherwise.
   */
  setOrganizations({ commit, state: s }: any, organizations: any[]) {
    const switchable = switchableOrganizations(organizations);
    commit('SET_ORGANIZATIONS', switchable);
    if (!s.organization) return;
    const current = switchable.find((org) => org.id === s.organization.id);
    commit('SET_ORGANIZATION', current ?? null);
  },

  /** Loads `profile.user.organizations` for the signed-in user. */
  async loadOrganizations({ dispatch, rootGetters }: any) {
    const token = rootGetters['users/getToken'];
    if (!token) return;
    // Lazy imports: @/graphs imports the store
    const [{ makeRequest }, { getUserOrganizations }] = await Promise.all([
      import('@/graphs/index'), import('@/graphs/profile'),
    ]);
    const response = await makeRequest('query', getUserOrganizations, {}, { token });
    if (response?.errors?.length) throw new Error(response.errors[0].message);
    await dispatch('setOrganizations', response?.data?.profile?.user?.organizations ?? []);
  },

  reset({ commit }: any) {
    commit('RESET');
  },
};

const getters = {
  organization: (s: ActingEntityState) => s.organization,
  organizations: (s: ActingEntityState) => s.organizations,
  organizationsLoaded: (s: ActingEntityState) => s.organizationsLoaded,
  isOrganization: (s: ActingEntityState) => !!s.organization,
  /** The acting organization's id, or undefined when acting as the user. */
  orgId: (s: ActingEntityState) => s.organization?.id,
  entityType: (s: ActingEntityState): ActingEntityType => (s.organization ? 'Organization' : 'User'),
  entityId: (s: ActingEntityState, _g: any, _rs: any, rootGetters: any): string | undefined => (
    s.organization?.id ?? rootGetters['users/me']?.id
  ),
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
